import React from "react";
import styled from "@emotion/styled";
import { useHistory, useLocation } from "react-router";
import { isScoreBeta } from "../utils/beta";
import useWindowSize from "../utils/useWindowSize";
import { trackPage } from "../utils/track";

/**
 * 새 디자인 아래쪽 메뉴 탭 (2026-10, 미리 써보기 계정 + 휴대폰 폭에서만).
 * 검색·내 정보는 상단바(Header)의 검색창·옆 메뉴를 연다 (window 이벤트).
 */
const ICONS: Record<string, string> = {
  home: "M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z",
  all: "M4 5h16M4 10h16M4 15h10M4 20h7",
  search: "M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zm9 2-4-4",
  list: "M9 18V6l11-2v12M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm11-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0z",
  me: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm-8 9a8 8 0 0 1 16 0",
};

const HIDE_ON = ["/notes", "/auth", "/login", "/billing"];

// 화면을 열 때마다 기록 (예전·새 디자인 공통). BottomTabs 는 항상 그려지므로 여기서 함께
export const RouteTracker: React.FC = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    trackPage(pathname);
  }, [pathname]);
  return null;
};

const BottomTabs: React.FC = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { width = 1980 } = useWindowSize();
  if (!isScoreBeta() || width > 1023 || HIDE_ON.some((p) => pathname.startsWith(p))) return null;

  const tabs = [
    { key: "home", label: "홈", active: pathname === "/", go: () => history.push("/") },
    { key: "all", label: "전체악보", active: pathname.startsWith("/full-sheet-music"), go: () => history.push("/full-sheet-music") },
    { key: "search", label: "검색", active: pathname.startsWith("/search"), go: () => window.dispatchEvent(new Event("cs-open-search")) },
    { key: "list", label: "연주목록", active: pathname.startsWith("/playlist"), go: () => history.push("/playlist") },
    { key: "me", label: "내 정보", active: false, go: () => window.dispatchEvent(new Event("cs-open-menu")) },
  ];
  return (
    <Bar>
      {tabs.map((t) => (
        <Tab key={t.key} type="button" active={t.active} onClick={t.go}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d={ICONS[t.key]} />
          </svg>
          {t.label}
        </Tab>
      ))}
    </Bar>
  );
};

export default BottomTabs;

const Bar = styled.nav`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 900;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  height: calc(62px + env(safe-area-inset-bottom));
  padding-bottom: env(safe-area-inset-bottom);
  box-sizing: border-box;
  background: #121318;
  border-top: 1px solid #22252d;
`;

const Tab = styled.button<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 0;
  background: none;
  padding: 0;
  cursor: pointer;
  outline: 0;
  font-size: 11px;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  color: ${({ active }) => (active ? "#8fb0ff" : "#7c8392")};

  & > svg {
    width: 22px;
    height: 22px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;
